import { useState } from "react";
import api from "../api/client";

/**
 * Look up recent openings at a company and prefill the application form.
 *
 * This is a data-entry shortcut, not job discovery. It is for someone who
 * already applied somewhere and is recording it, so the results are not
 * presented as opportunities and nothing links out to apply.
 *
 * Fires on click rather than on keystroke: the upstream free tier allows
 * only 25 requests a month.
 */
export default function JobPrefill({ company, onSelect }) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!company || company.trim().length < 2) return;
    setLoading(true);
    try {
      const { data } = await api.get("/job-search/by-company", {
        params: { company: company.trim() },
      });
      setResults(data);
    } catch {
      // Prefill is a convenience. On failure the user just types it in.
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={search}
        disabled={loading || !company || company.trim().length < 2}
        className="text-xs font-medium text-brand-violet dark:text-brand-violet-on-dark disabled:opacity-40"
      >
        {loading ? "Looking up roles..." : "Find the role you applied for"}
      </button>

      {results !== null && results.length === 0 && !loading && (
        <p className="text-xs text-gray-400 mt-1">
          No openings found. Enter the details yourself below.
        </p>
      )}

      {results && results.length > 0 && (
        <>
          <div className="flex items-center justify-between mt-2 mb-1">
            <span className="text-xs text-gray-400">
              {results.length} open {results.length === 1 ? "role" : "roles"}
            </span>
            {/* Escape hatch, so the list never traps someone mid-form */}
            <button
              type="button"
              onClick={() => setResults(null)}
              className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              Dismiss
            </button>
          </div>

          {/* Bounded scroll region. Capped in viewport units so a long list
              never pushes the rest of the form off a phone screen. */}
          <ul className="flex flex-col gap-1 overflow-y-auto max-h-[30vh] sm:max-h-56 rounded-md border border-gray-200 dark:border-white/10 p-1">
            {results.map((job) => (
              <li key={job.external_id}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(job);
                    setResults(null);
                  }}
                  className="w-full text-left px-2.5 py-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/5 transition"
                >
                  {/* truncate rather than wrap, so every row is one line high
                      and the list length stays predictable */}
                  <span className="block text-sm truncate">{job.job_title}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                    {[job.location_city, job.location_country]
                      .filter(Boolean)
                      .join(", ") || job.company}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
